import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import DefaultLayout from "../components/DefaultLayout";
import Cart from "../components/Cart";
import Product from "../components/Product";
import './Orders.css';
import config from '../config'
import { DownOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Switch, message } from 'antd';
import { useCurrency } from '../contexts/ValidationContext';


const Orders = () => {
    const apiBaseUrl = config.REACT_APP_API_BASE_URL;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tableNumber, setTableNumber] = useState(searchParams.get('table') || '');
    const [orderId, setOrderId] = useState('');
    const { currency } = useCurrency();

    // auto collapseSidebar
    useEffect(() => {
        window.dispatchEvent(new Event('collapseSidebar'));
    }, []);

    const [selectedCategory, setSelectedCategory] = useState("all");
    const [cartItems, setCartItems] = useState([]);
    const [categories, setCategories] = useState([]); // store category data
    const [itemsData, setItemsData] = useState([]); // store item data
    const [showCart, setShowCart] = useState(true); // show cart or not
    const [filteredItemsData, setFilteredItemsData] = useState([]);


    //quantity change animation
    const [quantityAnimKey, setQuantityAnimKey] = useState(null);
    const prevCartItemsRef = useRef(cartItems);

    useEffect(() => {
        const prev = prevCartItemsRef.current;
        cartItems.forEach(item => {
            const prevItem = prev.find(x => x.cartKey === item.cartKey);
            if (
                (prevItem && item.quantity !== prevItem.quantity)
                || (!prevItem)
            ) {
                setQuantityAnimKey(item.cartKey + '-' + Date.now());
            }
        });
        prevCartItemsRef.current = cartItems;
    }, [cartItems]);

    // category arrow
    const scrollRef = useRef();
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    const scrollLeft = () => {
        scrollRef.current.scrollBy({ left: -150, behavior: "smooth" });
    };
    const scrollRight = () => {
        scrollRef.current.scrollBy({ left: 150, behavior: "smooth" });
    };

    const checkArrows = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowLeft(el.scrollLeft > 0);
        setShowRight(el.scrollLeft + el.offsetWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        checkArrows();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener("scroll", checkArrows);
        window.addEventListener("resize", checkArrows);
        return () => {
            el.removeEventListener("scroll", checkArrows);
            window.removeEventListener("resize", checkArrows);
        };
    }, []);

    useEffect(() => {
        setTimeout(checkArrows, 100);
    }, [categories]);

    // show item name/ slug
    const [showSlug, setShowSlug] = useState(() => {
        const saved = localStorage.getItem('showSlug');
        return saved === 'true';
    });

    const getSortedItems = (items, sortBySlug) => {
        const sortKey = sortBySlug ? 'slug' : 'name';
        return items.slice().sort((a, b) =>
            ((a[sortKey] || '').localeCompare(b[sortKey] || '', 'en', { sensitivity: 'base' }))
        );
    };

    // Reordering when switching display
    const handleShowSlugChange = (checked) => {
        setShowSlug(checked);
        localStorage.setItem('showSlug', checked);
        Swal.fire({
            icon: 'info',
            title: checked ? 'Now displaying Slug' : 'Now displaying Name',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000
        });
    };

    // get category data n item data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [categoriesRes, itemsRes] = await Promise.all([
                    axios.get(`${apiBaseUrl}/api/menus/get-category`),
                    axios.get(`${apiBaseUrl}/api/products/get-product`)
                ]);
                setCategories(categoriesRes.data);

                const sortedItems = getSortedItems(itemsRes.data, showSlug);
                setItemsData(sortedItems);
                setFilteredItemsData(sortedItems);

                // console.log("Categories:", categoriesRes.data);
                // console.log("Items:", itemsRes.data);

            } catch (error) {
                console.error("Failed to fetch data:", error);
            }
        };
        fetchData();
    }, [showSlug]); // Reordering on showSlug change

    // Sync filteredItemsData when itemsData changes
    useEffect(() => {
        setFilteredItemsData(itemsData);
    }, [itemsData]);



    // save cart items to localStorage
    useEffect(() => {
        localStorage.setItem('adminCartItems', JSON.stringify(cartItems));
    }, [cartItems]);

    // search item name n category
    const handleSearch = (searchValue) => {
        const lowerSearch = (searchValue || '').toLowerCase();
        let filtered;
        if (lowerSearch) {
            filtered = itemsData.filter(item =>
                (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
                (item.category && item.category.toLowerCase().includes(lowerSearch)) ||
                (item.slug && item.slug.toLowerCase().includes(lowerSearch))
            );
        } else {
            filtered = itemsData;
        }

        filtered = getSortedItems(filtered, showSlug);
        setFilteredItemsData(filtered);
    };

    const filteredItems = selectedCategory === "all"
        ? filteredItemsData
        : filteredItemsData.filter(item => item.category === selectedCategory);

    // *********** cart ***********
    //get all tables
    const [tablesData, setTablesData] = useState([]);
    useEffect(() => {
        axios.get(`${apiBaseUrl}/api/tables/get-table`)
            .then(res => setTablesData(res.data))
            .catch(err => setTablesData([]));
    }, []);

    //get all orders
    const [allOrders, setAllOrders] = useState([]);
    useEffect(() => {
        axios.get(`${apiBaseUrl}/api/orders/get-order`)
            .then(res => setAllOrders(res.data))
            .catch(err => setAllOrders([]));
    }, []);

    // get table number with busy/available dot
    const [allTableNumbers, setAllTableNumbers] = useState([]);

    useEffect(() => {
        if (tablesData.length === 0) return;
        setAllTableNumbers(
            tablesData
                .filter(table => table.table_status) // only show available table
                .map(table => {
                    const isBusy = allOrders.some(order =>
                        String(order.order_tablenumber).trim() === String(table.table_number).trim() &&
                        order.order_status && order.order_voidstatus !== true &&
                        order.total > 0
                    );
                    return {
                        value: table.table_number,
                        status: isBusy ? 'busy' : 'available'
                    };
                })
        );
    }, [tablesData, allOrders]);

    // control cart width
    const getCartWidth = () => {
        if (window.innerWidth <= 800) return 300;
        if (window.innerWidth <= 1024) return 340;
        if (window.innerWidth <= 1280) return 390;
        return 400;
    };

    const [cartWidth, setCartWidth] = useState(getCartWidth());

    useEffect(() => {
        const handleResize = () => setCartWidth(getCartWidth());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    // get table orders for cart list to view history order
    const [tableOrders, setTableOrders] = useState([]);

    useEffect(() => {
        if (!tableNumber) return;
        axios.get(`${apiBaseUrl}/api/orders/get-order?table=${tableNumber}`)
            .then(res => {
                setTableOrders(res.data);
            })
            .catch(err => {
                setTableOrders([]);
                console.error(err);
            });
    }, [tableNumber]);

    // add to cart
    const addToCart = (item) => {
        const product = itemsData.find(p => p._id === item._id);
        const maxQty = product?.quantity ?? Infinity;
        const quantityUnit = product?.quantityUnit || 1;
        const cartKey = item._id + '_' + JSON.stringify(item.addOns || []) + '_' + (item.remarks || '');

        setCartItems(prevItems => {
            // cal same name item total
            const sameNameTotalQty = prevItems
                .filter(x => x.name === item.name)
                .reduce((sum, x) => sum + x.quantity, 0);

            if (sameNameTotalQty >= maxQty) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Maximum stock reached',
                    text: `There are only ${maxQty} pcs left in stock.`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 4000
                });
                return [...prevItems, { ...item, quantity: 1, cartKey, quantityUnit }];
            }

            // determine whether the same product, the same addon, the same remarks
            const exist = prevItems.find(x =>
                x._id === item._id &&
                JSON.stringify(x.addOns) === JSON.stringify(item.addOns) &&
                x.remarks === item.remarks
            );

            if (exist) {
                return prevItems.map(x =>
                    x.cartKey === exist.cartKey
                        ? { ...exist, quantity: exist.quantity + 1, quantityUnit: exist.quantityUnit || quantityUnit }
                        : x
                );
            } else {
                return [...prevItems, { ...item, quantity: 1, cartKey, quantityUnit }];
            }
        });
        // console.log('addToCart', { ...item, quantityUnit });
    };

    const increaseQuantity = (item) => {
        const product = itemsData.find(p => p._id === item._id);

        const est = product.quantity === null || product.quantity === undefined
            ? 'NIL'
            : Math.floor(product.quantity / (product.quantityUnit || 1));

        const maxQty = est ?? Infinity;

        const sameNameTotalQty = cartItems
            .filter(x => x.name === item.name)
            .reduce((sum, x) => sum + x.quantity, 0);

        if (sameNameTotalQty + 1 > maxQty) {
            Swal.fire({
                icon: 'warning',
                title: 'Maximum stock reached',
                text: `There are only ${maxQty} pcs left in stock.`,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 4000
            });
            return;
        }

        setCartItems(prevItems =>
            prevItems.map(x =>
                x.cartKey === item.cartKey
                    ? { ...x, quantity: x.quantity + 1, quantityUnit: x.quantityUnit || (itemsData.find(p => p._id === x._id)?.quantityUnit || 1) }
                    : x
            )
        );
    };

    const decreaseQuantity = (item) => {
        setCartItems(prevItems => {
            const exist = prevItems.find(x => x.cartKey === item.cartKey);
            if (!exist) return prevItems;

            if (exist.quantity === 1) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });

                return prevItems.filter(x => x.cartKey !== item.cartKey);
            } else {
                return prevItems.map(x =>
                    x.cartKey === item.cartKey
                        ? { ...x, quantity: x.quantity - 1, quantityUnit: x.quantityUnit || (itemsData.find(p => p._id === x._id)?.quantityUnit || 1) }
                        : x
                );
            }
        });
    };

    const removeFromCart = (item) => {
        setCartItems(prevItems => prevItems.filter(x => x.cartKey !== item.cartKey));
    };

    // place order
    const handleOrder = async () => {
        console.log('itemsData:', itemsData);

        // INVENTORY CHECK: Check stock availability before placing order
        console.log('🔍 CHECKING INVENTORY BEFORE PLACING ORDER...');
        const inventoryIssues = [];

        for (const item of cartItems) {
            // Check main product inventory
            const mainProduct = itemsData.find(p => p.pid === item.pid);
            if (mainProduct) {
                // Only check inventory if quantity is set (not null/undefined/empty)
                // If quantity is null/undefined/empty, treat as unlimited stock
                if (mainProduct.quantity != null && mainProduct.quantity !== '' && mainProduct.quantity < item.quantity) {
                    inventoryIssues.push({
                        type: 'main_product',
                        name: item.name,
                        required: item.quantity,
                        available: mainProduct.quantity,
                        message: `${item.name}: Required ${item.quantity}, Available ${mainProduct.quantity}`
                    });
                }
            } else {
                inventoryIssues.push({
                    type: 'main_product',
                    name: item.name,
                    required: item.quantity,
                    available: 0,
                    message: `${item.name}: Product not found in inventory`
                });
            }

            // Check add-ons inventory
            if (item.addOns && item.addOns.length > 0) {
                for (const addOn of item.addOns) {
                    // Skip inventory check for custom options (items without _id or pid)
                    // Custom options don't have inventory tracking
                    if (!addOn._id && !addOn.pid) {
                        console.log(`Skipping inventory check for custom option: ${addOn.name}`);
                        continue;
                    }

                    let addOnProduct = null;

                    // Try to find the add-on product
                    if (addOn.pid) {
                        addOnProduct = itemsData.find(p => p.pid === addOn.pid);
                    }
                    if (!addOnProduct && addOn._id) {
                        addOnProduct = itemsData.find(p => p._id === addOn._id);
                    }
                    if (!addOnProduct) {
                        addOnProduct = itemsData.find(p => p.name === addOn.name);
                    }

                    if (addOnProduct) {
                        // Only check inventory if quantity is set (not null/undefined/empty)
                        // If quantity is null/undefined/empty, treat as unlimited stock
                        if (addOnProduct.quantity != null && addOnProduct.quantity !== '' && addOnProduct.quantity < item.quantity) {
                            inventoryIssues.push({
                                type: 'addon',
                                name: addOn.name,
                                required: item.quantity,
                                available: addOnProduct.quantity,
                                message: `${addOn.name}: Required ${item.quantity}, Available ${addOnProduct.quantity}`
                            });
                        }
                    } else {
                        inventoryIssues.push({
                            type: 'addon',
                            name: addOn.name,
                            required: item.quantity,
                            available: 0,
                            message: `${addOn.name}: Add-on not found in inventory`
                        });
                    }
                }
            }
        }

        // If there are inventory issues, show error and stop order
        if (inventoryIssues.length > 0) {
            console.log('🚨 INVENTORY ISSUES FOUND:', inventoryIssues);
            const errorMessage = 'Order failed due to insufficient inventory:\n\n' +
                inventoryIssues.map(issue => `• ${issue.message}`).join('\n');

            message.error({
                content: errorMessage,
                duration: 10, // Show for 10 seconds
                style: { whiteSpace: 'pre-line' }
            });
            return; // Stop the order process
        }

        console.log('✅ INVENTORY CHECK PASSED - All items available');
        // no table number cannot order
        if (!tableNumber || String(tableNumber).trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Please select a table',
                text: 'Pick a table first',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3500
            });
            return;
        }

        const userId = localStorage.getItem('userId') || 'client';
        // if (!userId) {
        //     alert("Please enter user access key");
        //     return;
        // }

        // 1. Assemble order_items with the same structure as the original user side.
        if (cartItems.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Cart is empty',
                text: 'Please add items to the cart before placing an order.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        const uniqueNames = [...new Set(cartItems.map(item => item.name))];
        for (const name of uniqueNames) {
            const totalQty = cartItems
                .filter(item => item.name === name)
                .reduce((sum, item) => sum + item.quantity, 0);

            const product = itemsData.find(p => p.name === name);
            const maxQty = product?.quantity ?? Infinity;

            if (totalQty > maxQty) {
                Swal.fire({
                    icon: 'error',
                    title: 'Order Failed',
                    text: `There are only ${maxQty} pcs left in stock at ${name}, cannot place an order for ${totalQty} pcs`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 5000
                });
                return;
            }
        }

        const order_items = cartItems.map(item => {
            const addOns = (item.addOns || []).map(group => {
                // Convert ObjectId strings to numbers for backward compatibility
                // let id = group.id;
                // if (typeof id === 'string' && id.length === 24) {
                //     // This looks like a MongoDB ObjectId, convert to number
                //     id = Math.floor(Math.random() * 1000000);
                // }

                return {
                    group: group.group,
                    name: group.name,
                    price: group.price,
                    id: group.id, // Use converted ID
                    customUnit: group.customUnit,
                    pid: group.pid, // Preserve product ID for inventory tracking
                    _id: group._id, // Preserve MongoDB ID
                    isCategoryItem: group.isCategoryItem, // Preserve category item flag
                    printer: group.printer || [], // Ensure printer is included
                };
            }).filter(Boolean);

            // remark
            const remarksString = Array.isArray(item.remarks)
                ? item.remarks.map((remark) => `${remark.text}`).join(", ")
                : (item.remarks || "");

            // Product structure
            const product = {
                id: item._id || item.id,
                name: item.name,
                slug: item.slug || item.name?.toLowerCase().replace(/\s+/g, '-'),
                category: item.category,
                customUnit: item.customUnit,
                description: item.description || '',
                addOns: addOns,
                image: item.image || '',
                price: item.price,
                discountAble: item.discountAble || false,
                pid: item.pid,
                quantityUnit: item.quantityUnit || 1,
                isSubMenuPrint: item.isSubMenuPrint || false,
                // Include composite item fields for auto-deduction
                isComposite: item.isComposite || false,
                recipe: item.recipe || null,
            };

            // Validate required fields
            if (!product.pid) {
                console.error('Missing pid for item:', item);
                throw new Error(`Missing pid for item: ${item.name}`);
            }
            if (!product.name) {
                console.error('Missing name for item:', item);
                throw new Error(`Missing name for item`);
            }

            return {
                _id: item._id || item.id || (Date.now().toString(16) + Math.random().toString(16).slice(2)), // 保证唯一
                product,
                quantity: item.quantity ?? 1,
                remarks: remarksString,
                remark: remarksString,
                status: "Ordered",
                void_reason: '',
                user_id: userId || '',
            };
        });

        // 2. Calculation of the total price
        const total = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

        console.log('order_items:', JSON.stringify(order_items, null, 2));
        console.log('cartItems:', JSON.stringify(cartItems, null, 2));

        // 3. POST to backend
        console.log('🚀 === SENDING ORDER TO BACKEND ===');
        console.log('API URL:', `${apiBaseUrl}/api/orders/add`);
        console.log('Order data being sent:', {
            order_tablenumber: tableNumber,
            total,
            user_id: userId,
            order_items: order_items
        });

        // Debug: Log add-ons structure specifically
        console.log('🔍 DEBUG: Add-ons structure:');
        order_items.forEach((item, index) => {
            if (item.product.addOns && item.product.addOns.length > 0) {
                console.log(`Item ${index} add-ons:`, item.product.addOns.map(addon => ({
                    id: addon.id,
                    idType: typeof addon.id,
                    name: addon.name,
                    _id: addon._id,
                    pid: addon.pid,
                    customUnit: addon.customUnit,
                })));
            }
        });

        try {
            const res = await axios.post(`${apiBaseUrl}/api/orders/add`, {
                order_tablenumber: tableNumber,
                total,
                user_id: userId,
                order_items,
                void_reason: '',
                order_progressstatus: '',
            });
            console.log('✅ Order placed successfully:', res.data);
            console.log('Response status:', res.status);
            console.log('Response headers:', res.headers);

            // 4. Save order number, empty cart
            setOrderId(res.data.order_number);
            setCartItems([]);
            Swal.fire({
                title: 'Success',
                text: 'Order placed successfully.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            window.dispatchEvent(new Event('refreshItems'));
            navigate('/');
            localStorage.removeItem('adminCartItems');
        } catch (error) {
            console.error('Order error details:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Order failed: ${error.response?.data?.error || error.message || 'Please try again'}`,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5000
            });
        }
    };

    const [settings, setSettings] = useState({});
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get(`${config.REACT_APP_API_BASE_URL}/api/settings/get-settings`);
                setSettings(data);
            } catch (err) {
                console.error("Failed to fetch settings:", err);
            }
        };
        fetchSettings();
    }, []);

    return (
        <DefaultLayout
            searchPlaceholder="Name / Slug / Category" onSearch={handleSearch}
            showSlug={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>Name Display: </span>
                    <Switch
                        checked={showSlug}
                        onChange={handleShowSlugChange} checkedChildren="Slug"
                        unCheckedChildren="Name"
                        style={{
                            backgroundColor: showSlug ? '#1890ff' : '#52c41a',
                            borderColor: showSlug ? '#1890ff' : '#52c41a'
                        }}
                    />
                </div>
            }
        >
            <div style={{ display: 'flex', height: 'calc(100vh - 64px)', marginRight: showCart ? cartWidth : 0 }}>
                {/* Left side */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {/* category */}
                    <div style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        background: '#fff',
                        padding: '12px 0 8px 0',
                    }}>
                        <div className="category-bar-wrapper" style={{ position: 'relative' }}>
                            {showLeft && (
                                <button className="category-arrow left" onClick={scrollLeft}>
                                    <span className="category-arrow-icon">{'<'}</span>
                                </button>
                            )}
                            <div className="category-fade-left" />
                            <div
                                className="categories-container category-scroll"
                                ref={scrollRef}
                                style={{ marginLeft: 36 }}

                            >
                                <div
                                    className={`category-item ${selectedCategory === "all" ? "active" : ""}`}
                                    onClick={() => setSelectedCategory("all")}
                                >
                                    All
                                </div>
                                {categories.map(category => (
                                    <div
                                        key={category._id}
                                        className={`category-item ${selectedCategory === category.name ? "active" : ""}`}
                                        onClick={() => setSelectedCategory(category.name)}
                                    >
                                        {category.name}
                                    </div>
                                ))}
                            </div>
                            {showRight && (
                                <button className="category-arrow right" onClick={scrollRight}>
                                    <span className="category-arrow-icon">{'>'}</span>
                                </button>
                            )}
                            <div className="category-fade-right" />
                        </div>
                    </div>

                    {/* product */}
                    <div className="item-scroll-hide" style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '6px 0' }}>
                        <div className="product-grid">
                            {filteredItems.map(item => (
                                <div key={item._id} className="product-card">
                                    <Product
                                        item={item}
                                        addToCart={addToCart}
                                        showSlug={showSlug}
                                        currencyCode={currency}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right side cart */}
                {showCart && (
                    <div className="cart-fixed-container">
                        <Cart
                            tableNumber={tableNumber}
                            tableOrders={tableOrders}
                            orderId={orderId}
                            cartItems={cartItems}
                            setCartItems={setCartItems}
                            itemsData={itemsData}
                            increaseQuantity={increaseQuantity}
                            decreaseQuantity={decreaseQuantity}
                            removeFromCart={removeFromCart}
                            onOrder={handleOrder}
                            quantityAnimKey={quantityAnimKey}
                            setQuantityAnimKey={setQuantityAnimKey}
                            showSlug={showSlug}
                            currencyCode={currency}
                            setTableNumber={setTableNumber}
                            allTableNumbers={allTableNumbers}
                            settings={settings}
                        />
                    </div>
                )}

                {/* cart open/close button */}
                <button
                    onClick={() => setShowCart(v => !v)}
                    style={{
                        position: 'fixed',
                        right: showCart ? cartWidth : 10,
                        top: 65,
                        zIndex: 101,
                        background: '#ff7a00',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 12px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px #eee'
                    }}
                >
                    {showCart ? '>' : '<'}
                </button>
            </div>
        </DefaultLayout>
    );
};

export default Orders;